import axios, { AxiosPromise } from 'axios';
import ApiService from '@/api/http/ApiService';
import { AdyenPlatformComponent, StatementFormat } from '@/enums/SmoothrPay';
import {
  AddVenuePaymentMethodsRequest,
  AttachSplitConfigurationRequest,
  ComponentSessionResult,
  CreateMerchantConfigurationRequest,
  CreateVenueStoreRequest,
  HostedCheckoutUrlResult,
  MerchantAccount,
  MerchantConfiguration,
  MerchantConfigurationPagination,
  SplitConfiguration,
  StatementDownload,
  StatementSummary,
  UpdateBusinessLineRequest,
  VenuePaymentMethod,
  VenueStore,
} from '@/interfaces/models/SmoothrPay';

/** Page size used when looking a single configuration up by id. */
const LOOKUP_PAGE_SIZE = 100;

/** Safety net for the same lookup, so a bad id cannot page forever. */
const LOOKUP_MAX_PAGES = 50;

/**
 * Smoothr Pay onboarding service (Adyen for Platforms).
 *
 * The service mounts its routers at its own root (`/merchant-configurations`,
 * `/stores`, …) and the API gateway rewrites a prefix onto `/` — the same
 * arrangement as reporting-service. So the external base is
 * `v1/smoothr-pay-onboarding/…`, which is also what the new backoffice uses:
 * if routing lands on a different segment, the constructor below is the single
 * place to fix.
 */
export class SmoothrPayApiService extends ApiService<MerchantConfiguration> {
  constructor() {
    super('smoothr-pay-onboarding', 'v1');
  }

  // ── Merchant configurations ───────────────────────────────────────────────

  /**
   * Paginated, newest first. Scoped server-side: a regular user sees only what
   * they own, admins see everything.
   */
  public getConfigurations(
    params: { page?: number; limit?: number } = {},
  ): AxiosPromise<MerchantConfigurationPagination> {
    return axios.get(`${this.getBaseUrl()}/merchant-configurations`, { params });
  }

  /**
   * One configuration by id.
   *
   * The service exposes no `GET /merchant-configurations/:id` — only the
   * paginated list and `/:id/venues` — so the detail view finds its document by
   * paging the list, exactly as the new backoffice does. Scoping still applies:
   * a configuration the caller may not see is simply not in the pages.
   */
  public async findConfiguration(id: string): Promise<MerchantConfiguration | null> {
    for (let page = 1; page <= LOOKUP_MAX_PAGES; page++) {
      const { data } = await this.getConfigurations({ page, limit: LOOKUP_PAGE_SIZE });
      const found = (data.data || []).find((config: MerchantConfiguration) => config._id === id);
      if (found) {
        return found;
      }
      if (!data.data || data.data.length === 0 || page * LOOKUP_PAGE_SIZE >= (data.total || 0)) {
        break;
      }
    }
    return null;
  }

  /** Creates the configuration in `draft`; provisioning happens afterwards. */
  public createConfiguration(payload: CreateMerchantConfigurationRequest): AxiosPromise<MerchantConfiguration> {
    return axios.post(`${this.getBaseUrl()}/merchant-configurations`, payload);
  }

  /** Mails the merchant their onboarding invitation. Admin-only server-side. */
  public invite(id: string): AxiosPromise<MerchantConfiguration> {
    return axios.post(`${this.getBaseUrl()}/merchant-configurations/${id}/invite`);
  }

  /** Admin-only: puts a provisioned configuration back to `draft`. */
  public resetToDraft(id: string): AxiosPromise<MerchantConfiguration> {
    return axios.patch(`${this.getBaseUrl()}/merchant-configurations/${id}/draft`);
  }

  // ── Provisioning, in the order the service requires ───────────────────────

  public createLegalEntity(id: string): AxiosPromise<MerchantConfiguration> {
    return axios.post(`${this.getBaseUrl()}/merchant-configurations/${id}/legal-entity`);
  }

  public createBusinessLines(id: string): AxiosPromise<MerchantConfiguration> {
    return axios.post(`${this.getBaseUrl()}/merchant-configurations/${id}/business-lines`);
  }

  public createAccountHolder(id: string): AxiosPromise<MerchantConfiguration> {
    return axios.post(`${this.getBaseUrl()}/merchant-configurations/${id}/account-holder`);
  }

  public createBalanceAccounts(id: string): AxiosPromise<MerchantConfiguration> {
    return axios.post(`${this.getBaseUrl()}/merchant-configurations/${id}/balance-accounts`);
  }

  /**
   * Short-lived Adyen hosted onboarding link. Regenerating it is fine — the
   * link expires within minutes.
   */
  public createHostedCheckoutUrl(id: string): AxiosPromise<HostedCheckoutUrlResult> {
    return axios.post(`${this.getBaseUrl()}/merchant-configurations/${id}/hosted-checkout-url`);
  }

  /**
   * Session token for the embedded Adyen overviews. The service derives the
   * session's Adyen roles from the components asked for, so requesting only the
   * one on screen narrows the token. Short-lived — the SDK asks again when it
   * needs to re-authenticate.
   */
  public createComponentSession(
    id: string,
    components?: AdyenPlatformComponent[],
  ): AxiosPromise<ComponentSessionResult> {
    return axios.post(
      `${this.getBaseUrl()}/merchant-configurations/${id}/component-sessions`,
      components && components.length ? { components } : {},
    );
  }

  /**
   * Partial update of one business line. Omitted fields stay as they are, and
   * the merged line must still be valid — `webAddress` cannot be dropped while
   * `ecom` remains among the sales channels.
   */
  public updateBusinessLine(
    id: string,
    businessLineId: string,
    payload: UpdateBusinessLineRequest,
  ): AxiosPromise<MerchantConfiguration> {
    return axios.patch(`${this.getBaseUrl()}/merchant-configurations/${id}/business-lines/${businessLineId}`, payload);
  }

  // ── Venue stores ──────────────────────────────────────────────────────────

  public getMerchantVenues(id: string): AxiosPromise<VenueStore[]> {
    return axios.get(`${this.getBaseUrl()}/merchant-configurations/${id}/venues`);
  }

  public getMerchantAccounts(): AxiosPromise<MerchantAccount[]> {
    return axios.get(`${this.getBaseUrl()}/merchant-accounts`);
  }

  /**
   * Split-configuration profiles. They are defined per merchant account, so a
   * configuration assigned to a non-default account must scope the lookup.
   */
  public getSplitConfigurations(merchantAccount?: string): AxiosPromise<SplitConfiguration[]> {
    const url = `${this.getBaseUrl()}/split-configurations`;
    if (!merchantAccount) {
      return axios.get(url);
    }
    return axios.get(url, { params: { merchantAccount } });
  }

  /** Creates the venue's Adyen store. */
  public createVenueStore(payload: CreateVenueStoreRequest): AxiosPromise<VenueStore> {
    return axios.post(`${this.getBaseUrl()}/stores`, payload);
  }

  /** Attaches a profile to an existing store, replacing whatever it carried. */
  public attachSplitConfiguration(venue: string, payload: AttachSplitConfigurationRequest): AxiosPromise<VenueStore> {
    return axios.patch(`${this.getBaseUrl()}/stores/${venue}/split-configuration`, payload);
  }

  public deactivateVenueStore(venue: string): AxiosPromise<VenueStore> {
    return axios.patch(`${this.getBaseUrl()}/stores/${venue}/deactivate`);
  }

  public closeVenueStore(venue: string): AxiosPromise<VenueStore> {
    return axios.patch(`${this.getBaseUrl()}/stores/${venue}/close`);
  }

  // ── Payment methods per store ─────────────────────────────────────────────

  public getVenuePaymentMethods(venue: string): AxiosPromise<VenuePaymentMethod[]> {
    return axios.get(`${this.getBaseUrl()}/stores/${venue}/payment-methods`);
  }

  public addVenuePaymentMethods(
    venue: string,
    payload: AddVenuePaymentMethodsRequest = {},
  ): AxiosPromise<VenuePaymentMethod[]> {
    return axios.post(`${this.getBaseUrl()}/stores/${venue}/payment-methods`, payload);
  }

  /** The merchant's settlement overviews, newest period first. */
  public listStatements(id: string, limit?: number): AxiosPromise<StatementSummary[]> {
    return axios.get(`${this.getBaseUrl()}/merchant-configurations/${id}/statements`, {
      params: limit ? { limit } : {},
    });
  }

  /**
   * A signed link to one rendering of a statement, valid for 15 minutes.
   *
   * Answers `202` with `status: pending` when that format has not been rendered
   * yet — the render is queued and the same request serves the URL once it has
   * run, so the caller asks again rather than treating it as a failure.
   */
  public getStatementDownload(
    id: string,
    statementId: string,
    format: StatementFormat,
  ): AxiosPromise<StatementDownload> {
    return axios.get(`${this.getBaseUrl()}/merchant-configurations/${id}/statements/${statementId}/download`, {
      params: { format },
    });
  }

  public removeVenuePaymentMethod(venue: string, paymentMethodId: string): AxiosPromise<void> {
    return axios.delete(`${this.getBaseUrl()}/stores/${venue}/payment-methods/${paymentMethodId}`);
  }
}

export default new SmoothrPayApiService();
